/**
 * Campaign renames that have not yet been written onto a full sprint record.
 *
 * Sidebar/list rows are often metadata-only (no tasks). Saving those is skipped,
 * so a rename can show on the mission card and then snap back when the campaign
 * is opened and reloaded from the API. The pending name is kept in memory and
 * applied to that reload until a full save lands.
 */

const pendingNames = new Map();

export function markPendingSprintRename(sprintId, name) {
  if (!sprintId || typeof name !== 'string') return;
  pendingNames.set(sprintId, name);
}

export function peekPendingSprintRename(sprintId) {
  return pendingNames.has(sprintId) ? pendingNames.get(sprintId) : null;
}

export function clearPendingSprintRename(sprintId) {
  pendingNames.delete(sprintId);
}

export function _resetPendingSprintRenames() {
  pendingNames.clear();
}

/**
 * @param {object} sprint
 * @param {{ fromServer?: boolean }} [options]
 *   fromServer: clear the pending mark when the stored name already matches.
 *   In-memory overlays must not clear it — the server may still have the old name.
 */
export function applyPendingSprintRename(sprint, { fromServer = false } = {}) {
  if (!sprint?.id || !pendingNames.has(sprint.id)) return sprint;
  const name = pendingNames.get(sprint.id);
  if (sprint.name === name) {
    if (fromServer) pendingNames.delete(sprint.id);
    return sprint;
  }
  return { ...sprint, name };
}

/**
 * A campaign already loaded with tasks wins over a fetch that started while it
 * was still a metadata shell. Otherwise keep a rename the server doesn't have yet.
 */
export function mergeLoadedSprint(current, fetched) {
  if (current && Array.isArray(current.tasks)) {
    return applyPendingSprintRename(current);
  }
  if (!fetched) return current || null;
  return applyPendingSprintRename(fetched, { fromServer: true });
}
